import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, Keyboard, A11y, Mousewheel } from 'swiper/modules';

class YouTubeCarouselModal {
	constructor() {
		this.activeModal = null;
		this.previouslyFocused = null;

		this.onKeyDown = this.onKeyDown.bind( this );
	}

	open( modal, youtubeUrl ) {
		this.activeModal = modal;
		this.previouslyFocused = document.activeElement;

		const iframeWrap = modal.querySelector( '.youtube-carousel__modal-iframe-wrap' );
		iframeWrap.innerHTML = '';

		modal.hidden = false;
		document.body.style.overflow = 'hidden';
		document.addEventListener( 'keydown', this.onKeyDown );

		// Render the standard YouTube player. The first click opened this modal;
		// the viewer presses YouTube's native play button to start the video.
		const iframe = document.createElement( 'iframe' );
		iframe.src = `${ youtubeUrl }?rel=0`;
		iframe.allow = 'accelerometer; encrypted-media; gyroscope; picture-in-picture';
		iframe.allowFullscreen = true;
		iframe.setAttribute( 'frameborder', '0' );
		iframe.setAttribute( 'loading', 'lazy' );
		iframe.title = 'YouTube video player';
		iframeWrap.appendChild( iframe );

		// Focus the close button
		const closeBtn = modal.querySelector( '.youtube-carousel__modal-close' );
		if ( closeBtn ) {
			closeBtn.focus();
		}
	}

	close() {
		if ( ! this.activeModal ) return;

		const iframeWrap = this.activeModal.querySelector( '.youtube-carousel__modal-iframe-wrap' );
		iframeWrap.innerHTML = '';

		this.activeModal.hidden = true;
		document.body.style.overflow = '';
		document.removeEventListener( 'keydown', this.onKeyDown );

		if ( this.previouslyFocused ) {
			this.previouslyFocused.focus();
		}

		this.activeModal = null;
		this.previouslyFocused = null;
	}

	onKeyDown( e ) {
		if ( e.key === 'Escape' ) {
			this.close();
			return;
		}

		// Trap focus inside modal
		if ( e.key === 'Tab' && this.activeModal ) {
			const focusable = this.activeModal.querySelectorAll(
				'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'
			);

			if ( focusable.length === 0 ) return;

			const first = focusable[ 0 ];
			const last = focusable[ focusable.length - 1 ];

			if ( e.shiftKey ) {
				if ( document.activeElement === first ) {
					e.preventDefault();
					last.focus();
				}
			} else {
				if ( document.activeElement === last ) {
					e.preventDefault();
					first.focus();
				}
			}
		}
	}
}

class YouTubeCarousel {
	constructor( node ) {
		this.node = node;
		this.layout = node.dataset.layout || 'carousel';
		this.modal = node.querySelector( '.youtube-carousel__modal' );

		this.initThumbnailFallbacks();
		this.initItems();

		if ( this.layout === 'carousel' ) {
			this.initSwiper();
		}
	}

	initThumbnailFallbacks() {
		const images = this.node.querySelectorAll( 'img[data-thumbnail-candidates]' );

		images.forEach( ( image ) => {
			const candidates = ( image.dataset.thumbnailCandidates || '' )
				.split( '|' )
				.filter( Boolean );

			if ( candidates.length === 0 ) {
				return;
			}

			image.dataset.thumbnailIndex = String(
				Math.max( 0, candidates.indexOf( image.getAttribute( 'src' ) || '' ) )
			);

			image.addEventListener( 'error', () => {
				const index = parseInt( image.dataset.thumbnailIndex || '0', 10 );
				const nextIndex = Number.isNaN( index ) ? 0 : index + 1;

				if ( nextIndex >= candidates.length ) {
					return;
				}

				image.dataset.thumbnailIndex = String( nextIndex );
				image.src = candidates[ nextIndex ];
			} );
		} );
	}

	initItems() {
		// Use event delegation so dynamically cloned slides (Swiper loop mode)
		// also open the modal on a single click.
		this.node.addEventListener( 'click', ( e ) => {
			const item = e.target.closest( '.youtube-carousel__item' );

			if ( ! item || ! this.node.contains( item ) ) {
				return;
			}

			// Ignore clicks originating inside the modal itself.
			if ( item.closest( '.youtube-carousel__modal' ) ) {
				return;
			}

			e.preventDefault();
			const youtubeUrl = item.dataset.youtubeUrl;
			if ( youtubeUrl && this.modal ) {
				modalManager.open( this.modal, youtubeUrl );
			}
		} );

		// Close modal on backdrop click
		if ( this.modal ) {
			const backdrop = this.modal.querySelector( '.youtube-carousel__modal-backdrop' );
			if ( backdrop ) {
				backdrop.addEventListener( 'click', () => modalManager.close() );
			}

			const closeBtn = this.modal.querySelector( '.youtube-carousel__modal-close' );
			if ( closeBtn ) {
				closeBtn.addEventListener( 'click', () => modalManager.close() );
			}
		}
	}

	initSwiper() {
		const swiperEl = this.node.querySelector( '.youtube-carousel__swiper' );
		if ( ! swiperEl ) return;

		const slidesPerView = parseInt( this.node.dataset.slidesPerView, 10 ) || 3;
		const gap = parseInt( this.node.dataset.gap, 10 ) || 16;
		const autoplay = this.node.dataset.autoplay === 'true';
		const loop = this.node.dataset.loop === 'true';
		const pagination = this.node.dataset.pagination === 'true';

		const swiperConfig = {
			modules: [ Navigation, Pagination, Autoplay, Keyboard, A11y, Mousewheel ],
			slidesPerView: 1,
			spaceBetween: gap,
			grabCursor: true,
			// Allow a plain click/tap to reach our delegated handler so the modal
			// opens on a single click (Swiper otherwise swallows the first click).
			preventClicks: false,
			preventClicksPropagation: false,
			keyboard: {
				enabled: true,
			},
			// Smooth trackpad / mousewheel horizontal swiping. forceToAxis keeps
			// vertical page scrolling intact — only horizontal gestures move slides.
			mousewheel: {
				forceToAxis: true,
				sensitivity: 1,
				releaseOnEdges: true,
			},
			a11y: {
				prevSlideMessage: 'Previous video',
				nextSlideMessage: 'Next video',
			},
			navigation: {
				nextEl: swiperEl.querySelector( '.swiper-button-next' ),
				prevEl: swiperEl.querySelector( '.swiper-button-prev' ),
			},
			breakpoints: {
				768: {
					slidesPerView: Math.min( 2, slidesPerView ),
					spaceBetween: gap,
				},
				1024: {
					slidesPerView: slidesPerView,
					spaceBetween: gap,
				},
			},
		};

		if ( loop ) {
			swiperConfig.loop = true;
		}

		if ( pagination ) {
			swiperConfig.pagination = {
				el: swiperEl.querySelector( '.swiper-pagination' ),
				clickable: true,
			};
		}

		if ( autoplay ) {
			swiperConfig.autoplay = {
				delay: 5000,
				disableOnInteraction: true,
				pauseOnMouseEnter: true,
			};
		}

		this.swiper = new Swiper( swiperEl, swiperConfig );

		// Vertically center the prev/next buttons on the thumbnail rather than
		// the full slide height (which includes the title caption below).
		const positionNav = () => {
			const thumb = swiperEl.querySelector( '.youtube-carousel__thumb' );
			if ( thumb ) {
				swiperEl.style.setProperty( '--ytc-nav-top', `${ thumb.offsetHeight / 2 }px` );
			}
		};

		positionNav();
		this.swiper.on( 'resize', positionNav );
		this.swiper.on( 'imagesReady', positionNav );
		window.addEventListener( 'resize', positionNav );
	}
}

// Shared modal manager
const modalManager = new YouTubeCarouselModal();

// Initialize all instances
const carousels = document.querySelectorAll( '.youtube-carousel' );
carousels.forEach( ( node ) => new YouTubeCarousel( node ) );
